import React, { useState } from "react";
import { motion } from "framer-motion";
import { GALLERY_IMAGES, PAST_EVENTS } from "../lib/data";
import { ArrowUpRight, X } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const events = ["All", ...new Set(GALLERY_IMAGES.map((i) => i.event))];
  const visible = filter === "All"
    ? GALLERY_IMAGES
    : GALLERY_IMAGES.filter((i) => i.event === filter);

  return (
    <div data-testid="gallery-page">
      <section className="px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-12">
        <motion.div {...fadeUp} className="max-w-5xl">
          <p className="tracking-overline text-xs text-[#9A7456] mb-6">
            The Gallery
          </p>
          <h1
            className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.98]"
            data-testid="gallery-title"
          >
            A scrapbook of recent markets.
          </h1>
          <p className="font-body text-lg text-[#231F1E]/75 mt-8 max-w-2xl leading-relaxed">
            Photography by Kalvry. Browse a sampling below — or open the full
            collection on Pixieset.
          </p>
        </motion.div>
      </section>

      {/* Filters */}
      <section className="px-6 md:px-12 lg:px-16 pb-8 flex flex-wrap gap-2">
        {events.map((ev) => (
          <button
            key={ev}
            onClick={() => setFilter(ev)}
            data-testid={`gallery-filter-${ev.replace(/\s/g, '-').toLowerCase()}`}
            className={`px-5 py-2 rounded-full text-xs tracking-overline font-body transition-colors ${
              filter === ev
                ? "bg-[#231F1E] text-[#EDE6DA] border border-[#231F1E]"
                : "border border-[#231F1E]/25 text-[#231F1E] hover:bg-[#231F1E]/10"
            }`}
          >
            {ev}
          </button>
        ))}
      </section>

      {/* Masonry grid */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4 space-y-3 md:space-y-4">
          {visible.map((img, i) => (
            <motion.button
              key={img.src}
              onClick={() => setLightbox(img)}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 8) * 0.05 }}
              className="block w-full break-inside-avoid media-zoom"
              data-testid={`gallery-image-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-auto"
              />
            </motion.button>
          ))}
        </div>
      </section>

      {/* Pixieset links */}
      <section className="bg-[#231F1E] text-[#EDE6DA] px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <p className="tracking-overline text-xs text-[#D6B66B] mb-6">
          The Full Collections
        </p>
        <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-2xl mb-12">
          Want more? See every photo on Pixieset.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PAST_EVENTS.map((p) => (
            <a
              key={p.id}
              href={p.pixiesetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between border-t border-[#EDE6DA]/20 py-8 hover:border-[#D6B66B] transition-colors"
              data-testid={`pixieset-${p.id}`}
            >
              <div>
                <h3 className="font-display text-2xl md:text-3xl">{p.name}</h3>
                <p className="text-xs tracking-overline text-[#EDE6DA]/60 mt-2">
                  {p.date}
                </p>
              </div>
              <ArrowUpRight
                size={28}
                className="text-[#D6B66B] group-hover:rotate-45 transition-transform duration-500"
              />
            </a>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 bg-[#231F1E]/95 z-[100] flex items-center justify-center p-6 cursor-zoom-out"
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-[#EDE6DA] hover:text-[#D6B66B]"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-[92vw] max-h-[88vh] object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default Gallery;
